import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  MdAdd, MdChevronLeft, MdChevronRight, MdDownload, MdWarningAmber, MdEventBusy, MdSettings,
} from 'react-icons/md';
import {
  createCalendar, createEvent, exportIcs, getEvent, listCalendars, listOccurrences,
  scopedDelete, scopedUpdate,
} from '../api/calendar';
import { erroriPerCampo, messaggioErrore } from '../utils/apiError';
import {
  Badge, Button, Checkbox, DialogBody, DialogFooter, EmptyState, Field,
  IconButton, Modal, PageHeader, SkeletonList, Tabs,
} from '../components/ui';
import RecurrenceBuilder from '../components/calendar/RecurrenceBuilder';
import ScopeDialog from '../components/calendar/ScopeDialog';
import WeekGrid from '../components/calendar/WeekGrid';
import CalendarSettings from '../components/calendar/CalendarSettings';
import CalendarSharing from '../components/CalendarSharing';

const VISTE = [
  { value: 'giorno', label: 'Giorno' },
  { value: 'settimana', label: 'Settimana' },
  { value: 'mese', label: 'Mese' },
  { value: 'agenda', label: 'Agenda' },
];

const vuoto = {
  title: '', dtstart_local: '', dtend_local: '', tzid: 'Europe/Rome',
  rrule: '', all_day: false, location: '', notes: '',
};

const chiave = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const aMezzanotte = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const locale = d => `${chiave(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;

const FUSI = (() => {
  try { return Intl.supportedValuesOf('timeZone'); }
  catch { return ['Europe/Rome', 'Europe/London', 'UTC']; }
})();

function settimanaDi(data) {
  const primo = aMezzanotte(data);
  primo.setDate(primo.getDate() - ((primo.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const g = new Date(primo); g.setDate(primo.getDate() + i); return g;
  });
}

export function celleMese(anno, mese) {
  const primo = new Date(anno, mese, 1);
  primo.setDate(primo.getDate() - ((primo.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => {
    const g = new Date(primo); g.setDate(primo.getDate() + i); return g;
  });
}

export default function CalendarPage() {
  const [calendari, setCalendari] = useState([]);
  const [visibili, setVisibili] = useState([]);
  const [vista, setVista] = useState('agenda');
  const [fuoco, setFuoco] = useState(() => new Date());
  const [eventi, setEventi] = useState([]);
  const [avvisi, setAvvisi] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');

  const [bozza, setBozza] = useState(null);
  const [originale, setOriginale] = useState(null);
  const [ambito, setAmbito] = useState(null);
  const [nuovoCalendario, setNuovoCalendario] = useState(null);
  const [mostraAvvisi, setMostraAvvisi] = useState(false);
  const [erroriCampo, setErroriCampo] = useState({});
  const [erroreForm, setErroreForm] = useState('');
  const [impostazioni, setImpostazioni] = useState(null);

  const intervallo = useMemo(() => {
    if (vista === 'giorno') {
      const da = aMezzanotte(fuoco); const a = new Date(da); a.setDate(a.getDate() + 1);
      return [da, a];
    }
    if (vista === 'settimana') {
      const giorni = settimanaDi(fuoco);
      const a = new Date(giorni[6]); a.setDate(a.getDate() + 1);
      return [giorni[0], a];
    }
    if (vista === 'agenda') {
      const da = aMezzanotte(fuoco); const a = new Date(da); a.setDate(a.getDate() + 30);
      return [da, a];
    }
    const celle = celleMese(fuoco.getFullYear(), fuoco.getMonth());
    const a = new Date(celle[41]); a.setDate(a.getDate() + 1);
    return [celle[0], a];
  }, [vista, fuoco]);

  const caricaCalendari = useCallback(async () => {
    const dati = await listCalendars();
    setCalendari(dati);
    setVisibili(vecchi => (vecchi.length ? vecchi.filter(id => dati.some(c => c.id === id))
      : dati.map(c => c.id)));
  }, []);

  useEffect(() => { caricaCalendari().catch(e => setErrore(messaggioErrore(e))); }, [caricaCalendari]);

  const caricaEventi = useCallback(async () => {
    if (!visibili.length) { setEventi([]); setAvvisi([]); setCaricamento(false); return; }
    setCaricamento(true); setErrore('');
    try {
      const dati = await listOccurrences(visibili, intervallo[0].toISOString(), intervallo[1].toISOString());
      setEventi(dati.events); setAvvisi(dati.warnings);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricamento(false);
    }
  }, [visibili, intervallo]);

  useEffect(() => { caricaEventi(); }, [caricaEventi]);

  const esegui = async (azione, messaggio) => {
    setBusy(true); setErrore('');
    try {
      const esito = await azione();
      if (messaggio) toast.success(messaggio);
      await caricaEventi();
      return esito;
    } catch (e) {
      const testo = messaggioErrore(e);
      setErrore(testo); toast.error(testo);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const modificabili = calendari.filter(c => c.can_edit);
  const sposta = passo => {
    const d = new Date(fuoco);
    if (vista === 'mese') { d.setDate(1); d.setMonth(d.getMonth() + passo); }
    else if (vista === 'settimana') d.setDate(d.getDate() + 7 * passo);
    else d.setDate(d.getDate() + passo);
    setFuoco(d);
  };

  const apriNuovo = (quando) => {
    const inizio = quando || new Date(fuoco.getFullYear(), fuoco.getMonth(), fuoco.getDate(), 9);
    const fine = new Date(inizio); fine.setHours(fine.getHours() + 1);
    setOriginale(null);
    setBozza({
      ...vuoto,
      calendar: modificabili[0]?.id,
      dtstart_local: locale(inizio),
      dtend_local: locale(fine),
    });
  };

  const apriEsistente = async (occorrenza) => {
    const dati = await getEvent(occorrenza.event_id).catch(e => { setErrore(messaggioErrore(e)); return null; });
    if (!dati) return;
    setOriginale({ ...dati, recurrence_id: occorrenza.recurrence_id, ricorrente: !!dati.rrule });
    setBozza({
      calendar: dati.calendar, title: dati.title, dtstart_local: dati.dtstart_local,
      dtend_local: dati.dtend_local, tzid: dati.tzid, rrule: dati.rrule,
      all_day: dati.all_day, location: dati.location || '', notes: dati.notes || '',
    });
  };

  const chiudiForm = () => {
    setBozza(null); setOriginale(null); setAmbito(null);
    setErroriCampo({}); setErroreForm('');
  };

  const salva = async (scope = 'all') => {
    setBusy(true); setErroriCampo({}); setErroreForm('');
    try {
      if (!originale) {
        await createEvent({ ...bozza, calendar: Number(bozza.calendar) });
        toast.success('Evento creato.');
      } else {
        await scopedUpdate(originale.id, {
          ...bozza, scope, recurrence_id: originale.recurrence_id, version: originale.version,
        });
        toast.success('Evento aggiornato.');
      }
      await caricaEventi();
      chiudiForm();
    } catch (e) {
      // Il modulo non si chiude: quello che hai scritto resta e si corregge.
      setErroriCampo(erroriPerCampo(e));
      setErroreForm(messaggioErrore(e));
      setAmbito(null);
    } finally {
      setBusy(false);
    }
  };

  const elimina = async (scope = 'all') => {
    setBusy(true); setErroreForm('');
    try {
      await scopedDelete(originale.id, scope, originale.recurrence_id);
      toast.success('Evento eliminato.');
      await caricaEventi();
      chiudiForm();
    } catch (e) {
      setErroreForm(messaggioErrore(e));
      setAmbito(null);
    } finally {
      setBusy(false);
    }
  };

  // Spostando l'inizio si sposta anche la fine, come in ogni calendario:
  // altrimenti basta ritoccare l'ora per far diventare la fine precedente all'inizio.
  const cambiaInizio = (valore) => {
    const vecchio = bozza.dtstart_local ? new Date(bozza.dtstart_local) : null;
    const nuovo = valore ? new Date(valore) : null;
    const fine = bozza.dtend_local ? new Date(bozza.dtend_local) : null;
    if (vecchio && nuovo && fine && !Number.isNaN(nuovo.getTime())) {
      const spostata = new Date(fine.getTime() + (nuovo - vecchio));
      setBozza({
        ...bozza, dtstart_local: valore,
        dtend_local: bozza.all_day ? spostata.toISOString().slice(0, 10) : locale(spostata),
      });
      return;
    }
    setBozza({ ...bozza, dtstart_local: valore });
  };

  const perGiorno = useMemo(() => {
    const mappa = {};
    for (const e of eventi) {
      const k = e.all_day ? e.start_local.slice(0, 10) : chiave(new Date(e.start));
      (mappa[k] = mappa[k] || []).push(e);
    }
    return mappa;
  }, [eventi]);

  const titoloPeriodo = fuoco.toLocaleDateString('it-IT',
    vista === 'mese' ? { month: 'long', year: 'numeric' }
      : vista === 'giorno' ? { weekday: 'long', day: 'numeric', month: 'long' }
        : { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Calendario"
        subtitle="Più calendari insieme, ciascuno col suo colore. Gli orari sono nel fuso del tuo browser."
        actions={(
          <>
            <Button icon={MdDownload} disabled={!visibili.length}
              onClick={() => esegui(async () => {
                const blob = await exportIcs(visibili[0]);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'calendario.ics'; a.click();
                URL.revokeObjectURL(url);
              })}>
              Scarica ICS
            </Button>
            <Button variant="primary" icon={MdAdd} disabled={!modificabili.length}
              onClick={() => apriNuovo()}>Nuovo evento</Button>
          </>
        )} />

      {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

      <div className="flex flex-col gap-4 md:flex-row">
        <aside className="shrink-0 space-y-2 md:w-52">
          <details className="mobile-calendar-options"><summary>Calendari e condivisioni</summary><div className="space-y-2 pt-3">
          {calendari.map(c => (
            <div key={c.id} className="flex items-center gap-2">
              <span aria-hidden className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: c.color }} />
              <Checkbox checked={visibili.includes(c.id)} className="flex-1"
                onChange={e => setVisibili(e.target.checked
                  ? [...visibili, c.id] : visibili.filter(id => id !== c.id))}
                label={(
                  <span className="flex flex-wrap items-center gap-1">
                    {c.name}
                    {!c.can_edit && <Badge>sola lettura</Badge>}
                  </span>
                )} />
              <IconButton icon={MdSettings} label={`Impostazioni di ${c.name}`}
                className="h-8 w-8" size={16} onClick={() => setImpostazioni(c)} />
            </div>
          ))}
          <Button size="sm" icon={MdAdd} className="w-full"
            onClick={() => setNuovoCalendario({ name: '', color: '#16a34a' })}>
            Nuovo calendario
          </Button>
          {calendari.length > 0 && (
            <CalendarSharing calendar={calendari.find(c => c.id === visibili[0])} onChanged={caricaCalendari} />
          )}
          </div></details>
        </aside>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <IconButton icon={MdChevronLeft} label="Periodo precedente" onClick={() => sposta(-1)} />
            <Button size="sm" onClick={() => setFuoco(new Date())}>Oggi</Button>
            <IconButton icon={MdChevronRight} label="Periodo successivo" onClick={() => sposta(1)} />
            <strong className="px-2 text-sm capitalize text-ink">{titoloPeriodo}</strong>
            <span className="flex-1" />
            <Tabs tabs={VISTE} value={vista} onChange={setVista} label="Vista del calendario" />
          </div>

          {avvisi.length > 0 && (
            <div className="rounded-lg border border-warning/40 bg-warning-bg p-2">
              <button type="button" className="flex w-full items-center gap-2 text-left text-sm text-warning"
                onClick={() => setMostraAvvisi(!mostraAvvisi)} aria-expanded={mostraAvvisi}>
                <MdWarningAmber size={18} aria-hidden />
                {avvisi.length} eventi con orari da correggere — non sono mostrati
              </button>
              {mostraAvvisi && (
                <ul className="mt-2 space-y-1 text-xs text-warning">
                  {avvisi.map(a => (
                    <li key={a.event_id}>
                      <strong>{a.title}</strong> · {a.dtstart_local} — {a.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {caricamento ? <SkeletonList rows={4} /> : !visibili.length ? (
            <EmptyState icon={MdEventBusy} title="Nessun calendario selezionato"
              hint="Spunta almeno un calendario nell’elenco a sinistra." />
          ) : vista === 'settimana' || vista === 'giorno' ? (
            <WeekGrid days={vista === 'giorno' ? [aMezzanotte(fuoco)] : settimanaDi(fuoco)}
              events={eventi} onSlotClick={apriNuovo} onEventClick={apriEsistente} />
          ) : vista === 'mese' ? (
            <div className="grid grid-cols-7 gap-1">
              {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((g, i) => (
                <span key={i} className="text-center text-2xs text-muted">{g}</span>
              ))}
              {celleMese(fuoco.getFullYear(), fuoco.getMonth()).map(giorno => {
                const delMese = giorno.getMonth() === fuoco.getMonth();
                const diOggi = chiave(giorno) === chiave(new Date());
                const voci = perGiorno[chiave(giorno)] || [];
                return (
                  <div key={chiave(giorno)}
                    className={`min-h-24 rounded border border-line p-1 ${delMese ? '' : 'opacity-40'} ${diOggi ? 'ring-2 ring-brand-600' : ''}`}>
                    <button type="button" className="text-xs tabular-nums text-muted hover:text-ink"
                      onClick={() => { setFuoco(giorno); setVista('giorno'); }}>
                      {giorno.getDate()}
                    </button>
                    {voci.slice(0, 3).map((e, i) => (
                      <button key={i} type="button" onClick={() => apriEsistente(e)}
                        title={[e.title,
                          e.all_day ? 'tutto il giorno' : new Date(e.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
                          e.location].filter(Boolean).join(' — ')}
                        className="block w-full truncate rounded px-1 text-left text-2xs text-white"
                        style={{ backgroundColor: e.color || '#16a34a' }}>
                        {!e.all_day && `${new Date(e.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} `}
                        {e.title}
                      </button>
                    ))}
                    {voci.length > 3 && (
                      <button type="button" className="text-2xs text-muted"
                        onClick={() => { setFuoco(giorno); setVista('giorno'); }}>
                        altri {voci.length - 3}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <ul className="space-y-2">
              {eventi.map((e, i) => (
                <li key={i}>
                  <button type="button" onClick={() => apriEsistente(e)}
                    className="flex w-full items-start gap-3 rounded-lg border-l-4 border border-line bg-surface p-3 text-left"
                    style={{ borderLeftColor: e.color || '#16a34a' }}>
                    <span className="w-28 shrink-0 text-xs tabular-nums text-muted">
                      {new Date(e.start).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                      {!e.all_day && ` ${new Date(e.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`}
                    </span>
                    <span className="min-w-0">
                      <strong className="block truncate text-sm text-ink" title={e.title}>{e.title}</strong>
                      {e.location && <span className="text-xs text-muted">{e.location}</span>}
                    </span>
                  </button>
                </li>
              ))}
              {!eventi.length && <EmptyState icon={MdEventBusy} title="Nessun appuntamento nei prossimi 30 giorni" />}
            </ul>
          )}
        </div>
      </div>

      <Modal open={!!bozza} onClose={chiudiForm} size="lg"
        label={originale ? 'Modifica evento' : 'Nuovo evento'}
        description={originale?.ricorrente ? 'Fa parte di una serie: ti sarà chiesto cosa modificare.' : undefined}>
        <DialogBody className="space-y-3">
          {erroreForm && (
            <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{erroreForm}</p>
          )}
          <Field label="Titolo" required data-autofocus error={erroriCampo.title}
            value={bozza?.title || ''}
            onChange={e => setBozza({ ...bozza, title: e.target.value })} />
          <Field label="Calendario">
            {props => (
              <select {...props} value={bozza?.calendar || ''} disabled={!!originale}
                onChange={e => setBozza({ ...bozza, calendar: Number(e.target.value) })}>
                {modificabili.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </Field>
          <Checkbox label="Tutto il giorno" checked={bozza?.all_day || false}
            onChange={e => setBozza({ ...bozza, all_day: e.target.checked, dtstart_local: '', dtend_local: '' })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Inizio" required type={bozza?.all_day ? 'date' : 'datetime-local'}
              error={erroriCampo.dtstart_local || erroriCampo.dates}
              value={bozza?.dtstart_local || ''}
              onChange={e => cambiaInizio(e.target.value)} />
            <Field label={bozza?.all_day ? 'Fine (esclusa)' : 'Fine'} required
              error={erroriCampo.dtend_local}
              type={bozza?.all_day ? 'date' : 'datetime-local'} value={bozza?.dtend_local || ''}
              onChange={e => setBozza({ ...bozza, dtend_local: e.target.value })} />
          </div>
          <Field label="Fuso orario" hint="Serve quando l'evento è in un altro paese.">
            {props => (
              <select {...props} value={bozza?.tzid || 'Europe/Rome'}
                onChange={e => setBozza({ ...bozza, tzid: e.target.value })}>
                {FUSI.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            )}
          </Field>
          {erroriCampo.rrule && <p role="alert" className="text-xs text-danger">{erroriCampo.rrule}</p>}
          <RecurrenceBuilder rrule={bozza?.rrule || ''} inizio={bozza?.dtstart_local}
            onChange={rrule => setBozza({ ...bozza, rrule })} />
          <Field label="Luogo" value={bozza?.location || ''}
            onChange={e => setBozza({ ...bozza, location: e.target.value })} />
          <Field label="Note">
            {props => (
              <textarea {...props} rows={3} value={bozza?.notes || ''}
                onChange={e => setBozza({ ...bozza, notes: e.target.value })} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          {originale && (
            <Button variant="danger-ghost" className="mr-auto"
              onClick={() => (originale.ricorrente ? setAmbito('elimina') : elimina('all'))}>
              Elimina
            </Button>
          )}
          <Button variant="ghost" onClick={chiudiForm}>Annulla</Button>
          <Button variant="primary" loading={busy}
            onClick={() => (originale?.ricorrente ? setAmbito('modifica') : salva('all'))}>
            Salva
          </Button>
        </DialogFooter>
      </Modal>

      <ScopeDialog open={!!ambito} modo={ambito || 'modifica'} loading={busy}
        titolo={originale?.title} onClose={() => setAmbito(null)}
        onConfirm={scope => (ambito === 'elimina' ? elimina(scope) : salva(scope))} />

      <CalendarSettings calendar={impostazioni} open={!!impostazioni}
        onClose={() => setImpostazioni(null)} onChanged={caricaCalendari} />

      <Modal open={!!nuovoCalendario} onClose={() => setNuovoCalendario(null)} size="sm"
        label="Nuovo calendario">
        <DialogBody className="space-y-3">
          <Field label="Nome" required data-autofocus value={nuovoCalendario?.name || ''}
            onChange={e => setNuovoCalendario({ ...nuovoCalendario, name: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-ink">
            Colore
            <input type="color" aria-label="Colore del calendario" value={nuovoCalendario?.color || '#16a34a'}
              onChange={e => setNuovoCalendario({ ...nuovoCalendario, color: e.target.value })} />
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setNuovoCalendario(null)}>Annulla</Button>
          <Button variant="primary" loading={busy} disabled={!nuovoCalendario?.name?.trim()}
            onClick={() => esegui(async () => {
              await createCalendar(nuovoCalendario);
              await caricaCalendari();
              setNuovoCalendario(null);
            }, 'Calendario creato.')}>
            Crea
          </Button>
        </DialogFooter>
      </Modal>
    </div>
  );
}
